import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { Clock, Cloud, TerminalIcon } from "lucide-react";
import type { FpProject } from "../../../schemas";
import { formatShortDistance } from "../../lib/utils/date-utils";
import {
  formatDeploymentVersion,
  getVersionStatusColor,
} from "../../lib/utils/version-utils";

interface ProjectCardProps {
  project: FpProject;
  className?: string;
}

export function ProjectCard({ project, className }: ProjectCardProps) {
  const displayName = project.appName || <>&laquo;Untitled&raquo;</>;
  const isUntitled = !project.appName;
  const modifiedDate = new Date(project.updatedAt);

  const projectType = project.summary ? "codegen" : "cli";

  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      onClick={(e) => e.stopPropagation()}
      className="block @lg:aspect-[3/2]"
    >
      <Card
        className={cn(
          "h-full transition-all duration-200 hover:shadow-md cursor-pointer group border-border",
          "grid grid-rows-[28px_auto_32px] @md:grid-rows-[36px_auto_40px] gap-2 min-w-0",
          className,
        )}
      >
        <CardHeader className="contents p-0 space-y-0">
          <CardTitle
            className={cn(
              "text-base font-medium group-hover:text-primary transition-colors",
              "text-nowrap overflow-hidden text-ellipsis",
              "pt-1.5 @md:pt-3 px-4",
              isUntitled && "text-muted-foreground italic",
            )}
          >
            {displayName}
          </CardTitle>

          <CardDescription
            className="
                text-sm overflow-hidden
                [display:-webkit-box] [-webkit-box-orient:vertical]
                [--lines:1] [-webkit-line-clamp:var(--lines)]
                @md:[--lines:4] @lg:[--lines:3] @2xl:[--lines:4]
                px-4
              "
          >
            {project.summary}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-0">
          <div className="border-t px-4 py-2 @md:py-3 flex justify-between gap-2">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex items-center gap-1 text-xs",
                  getVersionStatusColor(project.latestDeployment),
                )}
              >
                <Cloud className="w-4 h-4" />{" "}
                {formatDeploymentVersion(project.latestDeployment)}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {projectType === "cli" && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <TerminalIcon className="w-3 h-3" /> CLI
                </div>
              )}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>{formatShortDistance(modifiedDate)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
